import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { format, startOfDay, endOfDay } from "date-fns";
import "../components/cgm-graph";
import "../components/entry-card";
import "../components/app-shell";
import { getActiveDb } from "../db/active-db";
import { Router } from "@vaadin/router";
import { authService } from "../services/auth";
import { selectedDateService } from "../services/selected-date";
import { monitoredUserSelectionService } from "../services/monitored-user-selection";
import type { LogEntry, CgmReading, DayNote } from "../types";
import { getQATotal, getBITotal, calculateIOB } from "../types";

/**
 * Dashboard page — high-level snapshot screen.
 *
 * Uses getActiveDb() so the same code path works for both the
 * user's own data and a monitored person's data.
 */
@customElement("dashboard-page")
export class DashboardPage extends LitElement {
  @state() private todayEntries: LogEntry[] = [];
  @state() private latestEntry: LogEntry | null = null;
  @state() private cgmReadings: CgmReading[] = [];
  @state() private clinicNotes: DayNote[] = [];
  @state() private userName = "";
  @state() private initialLoading = true;
  @state() private selectedDate: Date = selectedDateService.date;
  @state() private isMonitorView = false;
  private _selectedDateUnsubscribe?: () => void;
  private _monitorSelectionUnsubscribe?: () => void;

  static styles = css`
    :host {
      display: block;
    }

    .page {
      padding: 1rem;
      background: var(--color-bg, #f6faff);
    }

    h1 {
      margin: 0 0 1rem;
      font-size: 2.1rem;
      color: var(--color-text, #1f2d3d);
      line-height: 1.15;
    }

    h1 .accent {
      display: block;
      color: var(--color-accent, #bd64fc);
    }

    h2 {
      margin: 0 0 0.5rem;
      font-size: 1rem;
      color: #495057;
    }

    .stat-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 0.5rem;
      margin-bottom: 1rem;
      max-width: 28rem;
      margin-inline: auto;
    }

    .stat-card {
      background: var(--color-card);
      border-radius: 1rem;
      padding: 0.5rem;
      text-align: center;
      aspect-ratio: 1 / 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.25rem;
    }

    .stat-card .value {
      font-size: 1.75rem;
      font-weight: 700;
      line-height: 1.05;
      color: #1f2d3d;
    }

    .stat-card .label {
      font-size: 0.7rem;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    .section-card {
      background: #fff;
      border-radius: 1rem;
      padding: 1rem;
      margin-bottom: 1rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    }

    .note {
      border-left: 3px solid var(--color-accent);
      padding: 0.5rem 0.75rem;
      background: var(--color-accent-soft);
      border-radius: 0.25rem;
      margin-bottom: 0.5rem;
    }

    .note .author {
      font-size: 0.75rem;
      color: #6c757d;
      margin-bottom: 0.25rem;
    }

    .note .content {
      font-size: 0.9rem;
      color: #1f2d3d;
      white-space: pre-wrap;
    }

    .empty {
      color: #6c757d;
      font-size: 0.85rem;
      font-style: italic;
    }
  `;

  async connectedCallback() {
    super.connectedCallback();
    this.isMonitorView = monitoredUserSelectionService.isMonitorOnly;
    this.selectedDate = selectedDateService.date;

    this._selectedDateUnsubscribe = selectedDateService.subscribe((date) => {
      this.selectedDate = date;
      this._load(); // no loading flash on date change
    });
    this._monitorSelectionUnsubscribe =
      monitoredUserSelectionService.subscribe(() => {
        this.isMonitorView = monitoredUserSelectionService.isMonitorOnly;
        this._load();
      });

    if (this.isMonitorView) {
      await monitoredUserSelectionService.whenLoaded();
      await monitoredUserSelectionService.whenDataReady();
    }

    await this._load();
    this.initialLoading = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._selectedDateUnsubscribe?.();
    this._monitorSelectionUnsubscribe?.();
  }

  private async _load() {
    try {
      // Resolve display name
      if (this.isMonitorView) {
        this.userName =
          monitoredUserSelectionService.selected?.displayName || "";
      } else {
        const db = getActiveDb();
        const settings = (await db.settings.get("settings")) as any;
        const auth = authService.state;
        this.userName =
          settings?.nickname?.trim() || auth.displayName || auth.email || "";
      }

      const db = getActiveDb();
      const start = startOfDay(this.selectedDate);
      const end = endOfDay(this.selectedDate);

      // Entries for the selected day
      const allEntries = await db.entries.toArray();
      const valid = allEntries.filter((e) => !(e as any)._deletedAt);

      this.todayEntries = valid
        .filter((e) => {
          const ts = new Date(e.timestamp).getTime();
          return ts >= start.getTime() && ts <= end.getTime();
        })
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        ) as LogEntry[];

      // Latest entry for the selected day
      this.latestEntry = this.todayEntries[0] || null;

      // CGM readings for the selected day
      const allReadings = await db.cgmReadings.toArray();
      this.cgmReadings = allReadings
        .filter((r) => {
          const ts = new Date(r.timestamp).getTime();
          return ts >= start.getTime() && ts <= end.getTime();
        })
        .sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
        ) as CgmReading[];

      // Clinic notes
      let allNotes: DayNote[] = [];
      try {
        allNotes = ((await db.dayNotes.toArray()) || []).filter(
          (n) => !(n as any)._deletedAt,
        ) as DayNote[];
      } catch { /* dayNotes table may not exist */ }
      this.clinicNotes = allNotes
        .filter((n) => n.type === "clinic")
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )
        .slice(0, 3);
    } catch (err) {
      console.error("Dashboard _load error:", err);
    }
  }

  private _calculateActiveIOB(): number {
    const now = Date.now();
    let total = 0;
    for (const entry of this.todayEntries) {
      const qa = getQATotal(entry);
      if (qa) {
        const hoursElapsed =
          (now - new Date(entry.timestamp).getTime()) / (1000 * 60 * 60);
        if (hoursElapsed >= 0 && hoursElapsed <= 5) {
          total += calculateIOB(qa, hoursElapsed);
        }
      }
    }
    return Math.round(total * 10) / 10;
  }

  private _renderStats() {
    let carbs = 0;
    let qa = 0;
    let bi = 0;
    for (const e of this.todayEntries) {
      if (e.carbs) carbs += e.carbs;
      qa += getQATotal(e) || 0;
      bi += getBITotal(e) || 0;
    }
    const iob = this._calculateActiveIOB();
    return html`
      <div class="stat-grid">
        <div class="stat-card">
          <div class="value">${iob}u</div>
          <div class="label">IOB</div>
        </div>
        <div class="stat-card">
          <div class="value">${carbs}g</div>
          <div class="label">Carbs</div>
        </div>
        <div class="stat-card">
          <div class="value">${qa}u</div>
          <div class="label">Quick</div>
        </div>
        <div class="stat-card">
          <div class="value">${bi > 0 ? `${bi}u` : "-"}</div>
          <div class="label">Long</div>
        </div>
      </div>
    `;
  }

  private _renderLatestEntry() {
    if (!this.latestEntry) {
      return html`<p class="empty">No entries yet.</p>`;
    }
    return html`<entry-card
      .entry=${this.latestEntry}
      .allEntries=${this.todayEntries}
      ?hideEdit=${this.isMonitorView}
    ></entry-card>`;
  }

  private _renderClinicNotes() {
    if (this.clinicNotes.length === 0) {
      return html`<p class="empty">No clinician notes yet.</p>`;
    }
    return html`
      ${this.clinicNotes.map(
        (n) => html`
          <div class="note">
            <div class="author">
              ${n.authorName || "Clinician"} ·
              ${format(new Date(n.createdAt), "d MMM yyyy")} · ${n.date}
            </div>
            <div class="content">${n.content}</div>
          </div>
        `,
      )}
    `;
  }

  render() {
    // Monitor-only with no person selected — redirect to person selector
    if (
      this.isMonitorView &&
      monitoredUserSelectionService.loaded &&
      !monitoredUserSelectionService.selected
    ) {
      Router.go("/monitoring");
      return html``;
    }

    if (this.initialLoading) {
      return html`<app-shell pageTitle="Dashboard"
        ><p class="empty">Loading…</p></app-shell
      >`;
    }
    return html`
      <app-shell pageTitle="Dashboard">
        <header-date-stepper slot="header-center"></header-date-stepper>
        <div class="page">
        <h1>
          ${this.isMonitorView
            ? html`${this.userName}<span class="accent">Dashboard</span>`
            : html`${this.userName ? `${this.userName}'s` : ""}
                <span class="accent">Dashboard</span>`}
        </h1>
      ${this._renderStats()}

      <div class="section-card">
        <h2>Glucose</h2>
        <cgm-graph
          .readings=${this.cgmReadings}
          .entries=${this.todayEntries}
          .dateRange=${{
            start: startOfDay(this.selectedDate),
            end: endOfDay(this.selectedDate),
          }}
        ></cgm-graph>
      </div>

      <div class="section-card">
        <h2>Latest entry</h2>
        ${this._renderLatestEntry()}
      </div>

      <div class="section-card">
        <h2>Clinician notes</h2>
        ${this._renderClinicNotes()}
      </div>
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "dashboard-page": DashboardPage;
  }
}
